import * as React from "react";

import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";

import { UserGroupView, UserGroupOverviewResource } from "api/generated/model";
import { getUserGroups } from "api/generated/user-groups/user-groups";
import { SimpleAccessRights } from "components/accessRights";
import { StringInput } from "components/form/legacyFields/stringInput";
import { TextAreaInput } from "components/form/legacyFields/textAreaInput";
import {
    UserAndRights,
    UserGroupAccessRightsInstance,
    UserGroupAccessRights,
} from "utils/accessRights/models";
import { FormComponentProps, subForm } from "utils/form";

import { UserGroupCreateDetailsFields, UserGroupCreatorValues } from "./create";

type UserGroupDetailsMode =
    | ({
          type: "CREATE";
      } & FormComponentProps<
          "details",
          UserGroupCreateDetailsFields,
          UserGroupCreatorValues
      >)
    | {
          type: "EDIT";
          userGroup: UserGroupView;
          accessRights: UserAndRights<UserGroupAccessRights>[];
          // Only group admins (or organization admins) may change who administers or edits the
          // group. Editors see the current rights without being able to change them.
          canEditAccessRights: boolean;
          // Maybe replace with only one callback for the three
          onChangeName: (name: string | undefined) => Promise<void>;
          onChangeDescription: (name: string | undefined) => Promise<void>;
          onChangeAccessRights: (
              name: UserAndRights<UserGroupAccessRights>[]
          ) => Promise<void>;
      };

type UserGroupDetailsProps = UserGroupDetailsMode;

class UserGroupDetailsEditor extends React.PureComponent<UserGroupDetailsProps> {
    render = () => {
        const resourceAccessRights = UserGroupAccessRightsInstance;
        switch (this.props.type) {
            case "EDIT": {
                const {
                    onChangeName,
                    onChangeDescription,
                    onChangeAccessRights,
                    userGroup,
                    accessRights,
                    canEditAccessRights,
                } = this.props;
                return (
                    <>
                        <StringInput
                            id="name"
                            label={t("UserGroup_Name")}
                            type="EDIT"
                            value={userGroup.name}
                            onChange={onChangeName}
                            validators={[
                                {
                                    kind: "ruleset",
                                    required: true,
                                    whitespace: true,
                                },
                                {
                                    kind: "async",
                                    asyncValidator:
                                        buildNameAvailabilityChecker(userGroup),
                                },
                            ]}
                        />
                        <TextAreaInput
                            id="description"
                            label={t("UserGroup_Description")}
                            type="EDIT"
                            value={userGroup.description || ""}
                            onChange={onChangeDescription}
                            validators={[{ kind: "ruleset", whitespace: true }]}
                        />
                        {canEditAccessRights ? (
                            <SimpleAccessRights
                                type="EDIT"
                                id="accessRights"
                                value={accessRights}
                                onChange={onChangeAccessRights}
                                resourceAccessRights={resourceAccessRights}
                            />
                        ) : (
                            <SimpleAccessRights
                                type="READ_ONLY"
                                value={accessRights}
                                resourceAccessRights={resourceAccessRights}
                            />
                        )}
                    </>
                );
            }
            case "CREATE": {
                const { form } = this.props;
                const detailForm = subForm("details", form);
                return (
                    <>
                        <StringInput
                            id="name"
                            label={t("UserGroup_Name")}
                            type="CREATE"
                            initialValue={undefined}
                            validators={[
                                {
                                    kind: "ruleset",
                                    required: true,
                                    whitespace: true,
                                },
                                {
                                    kind: "async",
                                    asyncValidator:
                                        buildNameAvailabilityChecker(undefined),
                                },
                            ]}
                            form={detailForm}
                        />
                        <TextAreaInput
                            id="description"
                            label={t("UserGroup_Description")}
                            type="CREATE"
                            initialValue={undefined}
                            validators={[{ kind: "ruleset", whitespace: true }]}
                            form={detailForm}
                        />
                        <SimpleAccessRights
                            type="CREATE"
                            id="accessRights"
                            form={detailForm}
                            resourceAccessRights={resourceAccessRights}
                        />
                    </>
                );
            }
            default:
                return assertNever(this.props);
        }
    };
}

const buildNameAvailabilityChecker =
    (currentUserGroup: UserGroupView | undefined) =>
    (name: string | undefined): Promise<void> => {
        // If name is missing or did not change
        if (
            name === undefined ||
            (currentUserGroup && name === currentUserGroup.name)
        ) {
            return Promise.resolve();
        }

        return getUserGroups({ format: "Overview", name }).then((response) => {
            if (response.data.type !== "Overview") {
                throw new Error("Expected an overview user-group list");
            }
            // API returns all groups that matches the <name> pattern, so extra work is required
            // to check for the exact name match

            let groupWithSameName: UserGroupOverviewResource | undefined;
            if (currentUserGroup) {
                groupWithSameName = response.data.data.content
                    .filter(
                        (ug) => ug.resource.userGroup.id !== currentUserGroup.id
                    )
                    .find(
                        (ug) =>
                            ug.resource.userGroup.name.trim().toLowerCase() ===
                            name.trim().toLowerCase()
                    )?.resource;
            } else {
                groupWithSameName = response.data.data.content.find(
                    (ug) =>
                        ug.resource.userGroup.name.trim().toLowerCase() ===
                        name.trim().toLowerCase()
                )?.resource;
            }

            if (groupWithSameName === undefined) {
                return Promise.resolve();
            } else {
                return Promise.reject(
                    new Error(t("UserManagement_Groups_NameAlreadyExists"))
                );
            }
        });
    };

export { UserGroupDetailsEditor };
